import { ActiveSheetData } from '../../hooks/useActiveSheet';
import { ISheetShape } from '../../shapes/abstract/AbstractSheetShape';
import { mapPointsToPolygonShape } from '../../mappers/mapPointsToPolygonShape';
import {
  HoveredValidLineStyle,
  ValidLineStyle,
} from '../../SheetsViewerV2.config';
import { PolylineShape } from '../../shapes/PolylineShape';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';

export class MagicPolygonHandler {
  private results: any = null;
  private hasUserInteracted = false;
  private samplePoints: THREE.Vector3[] = [];

  updateResults(results: any) {
    this.results = results;
    // Reset interaction flag when new results are loaded
    this.hasUserInteracted = false;
  }

  reset() {
    this.results = null;
    this.hasUserInteracted = false;
    this.samplePoints = [];
  }

  hasValidResults(): boolean {
    return this.results && this.results.valid && this.results.multipolygon;
  }

  getResults(): any {
    return this.results;
  }

  shouldAddSamplePoint(hoveredPoint: any, hoveredLine: any): boolean {
    return !hoveredPoint && !hoveredLine;
  }

  addSamplePoint(point: THREE.Vector3) {
    this.samplePoints.push(point);
  }

  getSamplePoints(): THREE.Vector3[] {
    return this.samplePoints;
  }

  /**
   * Mark that the user has interacted with the shape (e.g., dragged vertices)
   */
  markUserInteraction(): void {
    this.hasUserInteracted = true;
  }

  /**
   * Check if there are edited points that differ from the original API results
   */
  hasEditedPoints(pointsArray: THREE.Vector3[][]): boolean {
    // If we have no API results, we can't have edited points
    if (!this.hasValidResults()) {
      return false;
    }

    // If we have no points array, we can't have edited points
    if (!pointsArray || pointsArray.length === 0 || !pointsArray[0]) {
      return false;
    }

    const currentPoints = pointsArray[0];
    const originalMultipolygon = this.results.multipolygon;

    // If the original multipolygon doesn't have the expected structure, we can't compare
    if (
      !originalMultipolygon.polygons ||
      originalMultipolygon.polygons.length === 0
    ) {
      return false;
    }

    const originalPolygon = originalMultipolygon.polygons[0];
    if (!originalPolygon.exterior || originalPolygon.exterior.length === 0) {
      return false;
    }

    // If the number of points is different, we have edited points
    if (currentPoints.length !== originalPolygon.exterior.length) {
      return true;
    }

    // Check if the user has actually interacted with the shape
    // This is the key indicator that points have been edited
    return this.hasUserInteracted;
  }

  /**
   * Convert the current edited points back to the format expected by mapDrawingResults
   * This ensures the same validation and processing pipeline as regular polygon drawing
   */
  convertEditedPointsToMultipolygon(
    pointsArray: THREE.Vector3[][],
    viewer: Autodesk.Viewing.Viewer3D,
    activeSheetData: ActiveSheetData
  ): any {
    if (!pointsArray || pointsArray.length === 0 || !pointsArray[0]) {
      return null;
    }

    const points = pointsArray[0];
    if (points.length < 3) {
      return null;
    }

    // Convert viewer coordinates back to API coordinates
    const converter = new ApiCoordinatesConverter(
      viewer,
      activeSheetData.viewport
    );
    const apiPoints = points
      .map((point) => converter.convertToApiPoint(point))
      .filter((point) => point !== null);

    if (apiPoints.length < 3) {
      return null;
    }

    // Return points in the format expected by mapDrawingResults
    // This will go through the same validation pipeline as regular polygon drawing
    return [apiPoints];
  }

  convertToEditablePoints(
    multipolygon: any,
    viewer: Autodesk.Viewing.Viewer3D,
    activeSheetData: ActiveSheetData
  ): THREE.Vector3[] {
    if (multipolygon.polygons && multipolygon.polygons.length > 0) {
      const polygon = multipolygon.polygons[0]; // Take the first polygon
      if (polygon.exterior && polygon.exterior.length > 0) {
        // Convert API coordinates back to viewer coordinates
        const converter = new ApiCoordinatesConverter(
          viewer,
          activeSheetData.viewport
        );
        const exteriorPoints = polygon.exterior
          .map((point: [number, number]) => {
            const apiPoint = { x: point[0], y: point[1] };
            return converter.convertToAutodeskPoint(apiPoint);
          })
          .filter((point: THREE.Vector3 | null) => point !== null); // Filter out null points

        return exteriorPoints;
      }
    }
    return [];
  }

  createShapes(
    multipolygon: any,
    commonArgs: [
      Autodesk.Viewing.Viewer3D,
      Autodesk.Extensions.Edit2D,
      ActiveSheetData
    ]
  ): ISheetShape[] {
    // Convert the multipolygon from API format to Edit2D shapes
    const shapes: ISheetShape[] = [];
    const [viewer, edit2d, activeSheetData] = commonArgs;

    if (multipolygon.polygons && multipolygon.polygons.length > 0) {
      multipolygon.polygons.forEach((polygon: any, polygonIndex: number) => {
        if (polygon.exterior && polygon.exterior.length > 0) {
          // Convert API coordinates back to viewer coordinates
          const converter = new ApiCoordinatesConverter(
            viewer,
            activeSheetData.viewport
          );
          const exteriorPoints = polygon.exterior
            .map((point: [number, number]) => {
              const apiPoint = { x: point[0], y: point[1] };
              return converter.convertToAutodeskPoint(apiPoint);
            })
            .filter((point: THREE.Vector3 | null) => point !== null); // Filter out null points

          // Only create shapes if we have valid points
          if (exteriorPoints.length > 0) {
            // Create a polygon shape from the exterior points
            const polygonShape = mapPointsToPolygonShape(
              [exteriorPoints], // pointsArray
              viewer,
              edit2d,
              activeSheetData
            );

            // Draw the polygon shapes to make them visible
            polygonShape.forEach((shape) => {
              shape.setSelected(true);
              shape.draw();
            });

            shapes.push(...polygonShape);

            // Also render interactive vertices and edges for editing
            this._renderMagicPolygonVerticesAndEdges(
              exteriorPoints,
              commonArgs,
              shapes
            );
          }
        }
      });
    }

    return shapes;
  }

  private _renderMagicPolygonVerticesAndEdges(
    points: THREE.Vector3[],
    commonArgs: [
      Autodesk.Viewing.Viewer3D,
      Autodesk.Extensions.Edit2D,
      ActiveSheetData
    ],
    shapes: ISheetShape[]
  ) {
    // Note: Vertex rendering is now handled by the main ShapesDrawingTool
    // This method only renders edges for the static API results

    // Render edges between consecutive points
    for (let i = 0; i < points.length - 1; i++) {
      const startPoint = points[i];
      const endPoint = points[i + 1];

      // Skip if either point is null or undefined
      if (!startPoint || !endPoint) continue;

      const edgeShape = new PolylineShape(
        ...commonArgs,
        undefined,
        [startPoint, endPoint],
        {
          default: ValidLineStyle,
          hovered: HoveredValidLineStyle,
          selected: ValidLineStyle,
        }
      );
      edgeShape.draw();
      shapes.push(edgeShape);
    }

    // Close the polygon if needed
    if (points.length > 2 && !points[0].equals(points[points.length - 1])) {
      const startPoint = points[points.length - 1];
      const endPoint = points[0];

      // Skip if either point is null or undefined
      if (!startPoint || !endPoint) return;

      const edgeShape = new PolylineShape(
        ...commonArgs,
        undefined,
        [startPoint, endPoint],
        {
          default: ValidLineStyle,
          hovered: HoveredValidLineStyle,
          selected: ValidLineStyle,
        }
      );
      edgeShape.draw();
      shapes.push(edgeShape);
    }
  }
}
